import clsx from "clsx";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { CommentGridBody } from "./CommentGridBody";
import { CommentGridHeader } from "./CommentGridHeader";
import { CommentSearchArea } from "./CommentSearchArea";

import { COMMENT_NUM_PER_PAGE } from "@/components/comment/constants/comment";
import { CommentState } from "@/components/comment/reducers/comment";
import { FooterPager } from "@/components/common/FooterPager";
import { useDebounceLoading } from "@/hooks/useDebounceLoading";
import { usePager } from "@/hooks/usePager";
import { RootState } from "@/reducers/types";

export const Comment = () => {
  const { t } = useTranslation();

  // データ
  const { list, total, fetching } = useSelector<RootState, CommentState>((state: RootState) => state.comment);

  const pager = usePager({ limit: COMMENT_NUM_PER_PAGE, total });

  // データ編集（削除・更新）による検索
  const refresh = (option?: { delete?: boolean }) => {
    if (option?.delete && 1 < pager.current && total % COMMENT_NUM_PER_PAGE === 1) {
      // 表示が1件しかない場合は前のページを検索する
      pager.prev();
    } else {
      pager.jump(pager.current);
    }
  };

  // 表示制御
  // 検索条件の表示/非表示
  const [showSearch, setShowSearch] = useState(true);
  const toggleSearch = () => setShowSearch(!showSearch);
  // ローディング表示
  const { loading } = useDebounceLoading(fetching);

  return (
    <>
      <div className="contents comment">
        <div className="inner flex flex-col overflow-hidden h-[calc(100%-95px)]" /*高さは共通ヘッダとフッタの分を引く*/>
          {/* タイトル */}
          <h1 className="page-ttl">
            {t("comment_list")}
            <span
              data-test-id="button-qr-toggle-search"
              className={`toggle icon-keyboard_arrow_up ${!showSearch ? "closed" : ""}`}
              onClick={() => toggleSearch()}
            />
          </h1>
          {/* 検索エリア */}
          <CommentSearchArea
            open={showSearch}
            onReset={pager.init}
            onSearch={pager.init}
            setPage={(p) => pager.setCurrent(p)}
          />
          {/* コメント一覧 */}
          <div
            className={clsx("relative grow overflow-auto", loading ? "loading" : "")} // 検索中
          >
            {list === null ? (
              // 未検索
              <></>
            ) : list.length === 0 ? (
              // データなし
              <p className="empty-message">{t("no_data")}</p>
            ) : (
              <>
                <CommentGridHeader />
                <CommentGridBody onRefresh={refresh} />
              </>
            )}
          </div>
        </div>
      </div>
      {/* フッタ */}
      {0 < pager.maxPage && (
        <div className="paging-footer">
          <FooterPager
            currentPage={pager.current}
            maxPage={pager.maxPage}
            onPrev={pager.prev}
            onNext={pager.next}
            onJump={pager.jump}
          />
        </div>
      )}
    </>
  );
};
